import {
  HttpErrorResponse,
  HttpHandlerFn,
  HttpInterceptorFn,
  HttpRequest,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { AuthService } from '../services/auth.service';
import { catchError, filter, switchMap, take, throwError } from 'rxjs';
import {
  Environment,
  RefreshTokenRequest,
  DEFAULT_HEADERS,
} from '@t-llevo/util';
import { apiRoutes } from '../api-routing';

export const authBaseInterceptor: HttpInterceptorFn = (req, next) => {
  const authSrv = inject(AuthService);
  const env = inject(Environment);

  const token: string | null | undefined = authSrv.getAuthToken();
  let authReq = req;
  if (token && !authReq.url.includes(env.refreshTokenUrl)) {
    authReq = addTokenHeader(req, env.tokenHeaderKey, token);
  }

  return next(authReq).pipe(
    catchError((error) => {
      if (
        error instanceof HttpErrorResponse &&
        !authReq.url.includes(env.refreshTokenUrl) &&
        !authReq.url.includes(apiRoutes.routeLogin) &&
        (error.status === 401 || error.status === 403)
      ) {
        return handle401Error(authReq, next, error, authSrv, env);
      }
      return throwError(() => error as HttpErrorResponse);
    })
  );
};

export const handle401Error = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  request: HttpRequest<any>,
  next: HttpHandlerFn,
  previousError: HttpErrorResponse,
  authSrv: AuthService,
  env: Environment
) => {
  if (!authSrv.isRefreshing) {
    authSrv.isRefreshing = true;
    const token = authSrv.getRefreshToken();
    if (token) {
      return authSrv
        .refreshToken<RefreshTokenRequest>(
          {
            url: `${env.apiUrl}${apiRoutes.routeRefresh}`,
            headers: { ...DEFAULT_HEADERS },
          },
          { refresh: token }
        )
        .pipe(
          switchMap((tokenRep) => {
            authSrv.isRefreshing = false;
            return next(
              addTokenHeader(request, env.tokenHeaderKey, tokenRep.access)
            );
          }),
          catchError((err) => {
            authSrv.isRefreshing = false;
            authSrv.localLogOut(); // TODO: hacer logout
            return throwError(() => err as HttpErrorResponse);
          })
        );
    }
    return throwError(() => previousError);
  }

  return authSrv.refresh$.pipe(
    filter((token) => (token ? true : false)),
    take(1),
    switchMap((token) =>
      next(addTokenHeader(request, env.tokenHeaderKey, token ?? 'NO_TOKEN'))
    )
  );
};

export const addTokenHeader = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  request: HttpRequest<any>,
  tokenHeader: string,
  token: string
) => {
  return request.clone({
    headers: request.headers.set(tokenHeader, 'Bearer ' + token),
  });
};
